import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Loader2, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Single-file document uploader (PDF / DOCX / XLSX) — used for tech sheets and brochures.
 * Props:
 *  - value: { url, filename, size, storage_path } | null
 *  - onChange: (newValue | null) => void
 *  - token: admin bearer token
 *  - label: "Ficha técnica" | "Brochure"
 *  - testid: unique id suffix
 */
export default function DocumentUploader({ value, onChange, token, label, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const upload = async (file) => {
    if (file.size > 20 * 1024 * 1024) { toast.error('Supera 20MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'docs');
    try {
      const res = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      onChange({ url: data.url, filename: data.filename, size: data.size, storage_path: data.storage_path });
      toast.success(`${label} subida`);
    } catch (e) {
      toast.error(e.message);
    }
    setUploading(false);
  };

  const remove = async () => {
    if (value?.storage_path) {
      try {
        await fetch(`${API}/admin/upload?storage_path=${encodeURIComponent(value.storage_path)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) { /* silent */ }
    }
    onChange(null);
  };

  const displayUrl = value?.url?.startsWith('/api/') ? `${BACKEND}${value.url}` : value?.url;
  const downloadUrl = value?.url?.startsWith('/api/') ? `${BACKEND}${value.url}?download=1` : value?.url;

  return (
    <div data-testid={`doc-uploader-${testid}`}>
      <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">{label}</label>
      {value ? (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
          <FileText className="w-6 h-6 text-[#0EA5E9] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#0A2540] truncate">{value.filename || 'Documento'}</div>
            <div className="text-[10px] text-slate-500">{Math.round((value.size || 0) / 1024)} KB</div>
          </div>
          <a href={displayUrl} target="_blank" rel="noopener noreferrer" title="Ver" className="p-1.5 rounded-md hover:bg-white" data-testid={`doc-view-${testid}`}>
            <ExternalLink className="w-4 h-4 text-[#0EA5E9]" />
          </a>
          <a href={downloadUrl} title="Descargar" className="p-1.5 rounded-md hover:bg-white" data-testid={`doc-download-${testid}`}>
            <Download className="w-4 h-4 text-[#0A2540]" />
          </a>
          <button type="button" onClick={remove} title="Eliminar" className="p-1.5 rounded-md hover:bg-red-50" data-testid={`doc-remove-${testid}`}>
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0EA5E9] hover:bg-sky-50 text-sm text-slate-500 transition-colors disabled:opacity-50"
          data-testid={`doc-upload-${testid}`}
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          {uploading ? 'Subiendo…' : `Subir ${label} (PDF, DOCX — máx 20MB)`}
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) { upload(e.target.files[0]); e.target.value = ''; } }}
      />
    </div>
  );
}
